import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useDropdownMenuKeyboardNav from '../../utils/useDropdownMenuKeyboardNav';
import { CaretSolidIcon, SecureIcon, ValidIcon, ValidationRequiredIcon, WarningIcon } from '@parchment/icons';
import Skeleton from 'react-loading-skeleton';
import { alto, apple, bondiBlue, boulder, breakpoints, catskillWhite, fairPink, frenchPass, GridContainerContext, java, mercury, mineShaft, thunderBird, white, willowBrook } from '@parchment/atoms';
import { CONTAINER_SIZES, EVENT_KEYS, FONT_SIZES, LINE_HEIGHT_SIZES, RADIUS_SIZES, SCALE, VALIDATION_STATES } from '../../utils/constants';
import { FocusRing } from '@react-aria/focus';
const noop = () => undefined;
export default function Dropdown(_ref) {
  let {
    autoComplete,
    deselectText = 'Clear Selection',
    disableBorder = false,
    disableDeselect = true,
    id,
    isDisabled = false,
    isRequired = false,
    loading = false,
    minWidth = '150px',
    maxMenuHeight = '250px',
    name,
    onBlur = noop,
    onChange = noop,
    onFocus = noop,
    options = [],
    placeholder,
    scale = 'medium',
    showRequiredInPlaceholder = true,
    valid = VALIDATION_STATES.neutral,
    value = undefined,
    visited,
    optionMatcher
  } = _ref;
  const [indexedOptions, setIndexedOptions] = useState([...options.map((opt, i) => ({
    ...opt,
    renderIndex: i,
    selectionIndex: i
  }))]);
  const [renderedOptions, setRenderedOptions] = useState([...indexedOptions]);
  const [dropdownMenuVisible, setDropdownMenuVisible] = useState(false);
  const [typedValue, setTypedValue] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(undefined);
  const [selectedIndex, setSelectedIndex] = useState(() => getItemIndex(value, options, optionMatcher));
  const toggleRef = useRef(null);
  const firstFocusRef = useRef(null);
  const focusedRef = useRef(null);
  const selectedRef = useRef(null);
  useEffect(() => {
    var _options$selectedInde;
    setIndexedOptions([...options.map((opt, i) => ({
      ...opt,
      renderIndex: i,
      selectionIndex: i
    }))]);
    if (selectedIndex && value !== ((_options$selectedInde = options[selectedIndex]) === null || _options$selectedInde === void 0 ? void 0 : _options$selectedInde.value)) {
      setFocusedIndex(undefined);
    }
  }, [options]);
  useEffect(() => {
    if (dropdownMenuVisible) {
      if (!typedValue) {
        setFocusedIndex({
          renderIndex: (() => {
            var _indexedOptions$filte;
            return ((_indexedOptions$filte = indexedOptions.filter(opt => opt.selectionIndex === selectedIndex)[0]) === null || _indexedOptions$filte === void 0 ? void 0 : _indexedOptions$filte.renderIndex) || 0;
          })(),
          keyboard: false,
          behavior: 'auto'
        });
        (firstFocusRef === null || firstFocusRef === void 0 ? void 0 : firstFocusRef.current) && firstFocusRef.current.focus();
        if (selectedRef !== null && selectedRef !== void 0 && selectedRef.current) {
          var _selectedRef$current;
          selectedRef.current.focus && selectedRef.current.focus();
          ((_selectedRef$current = selectedRef.current) === null || _selectedRef$current === void 0 ? void 0 : _selectedRef$current.scrollIntoView) && selectedRef.current.scrollIntoView({
            behavior: focusedIndex === null || focusedIndex === void 0 ? void 0 : focusedIndex.behavior,
            block: 'nearest'
          });
        }
      }
    } else {
      setTypedValue('');
      setFocusedIndex(undefined);
    }
  }, [dropdownMenuVisible]);
  useEffect(() => {
    if (value) {
      setSelection(value);
    } else {
      setSelectedIndex(undefined);
    }
  }, [value]);
  useEffect(() => {
    // trigger blur on first render if we have selected an item
    // this in turn will cause validation to run and make the field
    // green if valid
    if (typeof selectedIndex !== 'undefined') {
      onBlur && onBlur();
    }
  }, []);
  useEffect(() => {
    const opts = indexedOptions.filter(opt => {
      var _opt$label, _opt$value;
      return !typedValue || ((_opt$label = opt.label) === null || _opt$label === void 0 ? void 0 : _opt$label.toString().toLowerCase().includes(typedValue.toLowerCase())) || ((_opt$value = opt.value) === null || _opt$value === void 0 ? void 0 : _opt$value.toString().toLowerCase().includes(typedValue.toLowerCase()));
    });
    setRenderedOptions(opts.map((opt, i) => ({
      ...opt,
      renderIndex: i
    })));
  }, [typedValue, indexedOptions]);
  useEffect(() => {
    onChange(selectedIndex === undefined ? undefined : indexedOptions[selectedIndex].value);
    setDropdownMenuVisible(false);
    !!typedValue && setTypedValue('');
  }, [selectedIndex]);
  useEffect(() => {
    var _focusedRef$current;
    if ((_focusedRef$current = focusedRef.current) !== null && _focusedRef$current !== void 0 && _focusedRef$current.scrollIntoView && focusedIndex !== null && focusedIndex !== void 0 && focusedIndex.keyboard) {
      focusedRef.current.scrollIntoView({
        behavior: focusedIndex.behavior,
        block: 'nearest'
      });
      // this focus actually assists with removing the tooltip on selection with space bar
      focusedRef.current.focus();
    }
  }, [focusedRef.current]);
  useEffect(() => {
    if (visited && onBlur) {
      var _focusedRef$current2;
      // adding this focusedRef blur seems to help at least with a second selection and visit to the dropdown
      // with the enter key/arrow down
      focusedRef === null || focusedRef === void 0 ? void 0 : (_focusedRef$current2 = focusedRef.current) === null || _focusedRef$current2 === void 0 ? void 0 : _focusedRef$current2.blur();
      onBlur();
    }
  }, [selectedIndex, visited]);
  const createOptionRef = opt => {
    if ((focusedIndex === null || focusedIndex === void 0 ? void 0 : focusedIndex.renderIndex) === opt.renderIndex) return focusedRef;
    if (selectedIndex === opt.selectionIndex) return selectedRef;
    if (selectedIndex === undefined && focusedIndex === undefined && opt.renderIndex === 0) return firstFocusRef;
    return null;
  };
  const createClearOptionRef = () => {
    if (!disableDeselect && focusedIndex === undefined) {
      return firstFocusRef;
    }
    if ((focusedIndex === null || focusedIndex === void 0 ? void 0 : focusedIndex.renderIndex) === -1) {
      return focusedRef;
    }
    return null;
  };
  const setSelection = value => {
    const newIndex = getItemIndex(value, options, optionMatcher);
    if (newIndex !== selectedIndex) {
      setSelectedIndex(newIndex);
      onBlur && onBlur();
    }
  };
  const handleFocus = () => {
    if (isDisabled) return;
    const current = dropdownMenuVisible;
    setDropdownMenuVisible(!current);
    if (current) {
      onBlur && onBlur();
    } else {
      onFocus && onFocus();
    }
  };
  const handleBlur = e => {
    if (e && !e.currentTarget.contains(e.relatedTarget)) {
      setTypedValue('');
      setDropdownMenuVisible(false);
      onBlur && onBlur(e);
    }
  };
  const handleTextEntry = e => {
    var _firstFocusRef$curren, _toggleRef$current2;
    if (e.key === EVENT_KEYS.tab && !typedValue) return;
    if (e.key === EVENT_KEYS.backspace && !typedValue) {
      setSelectedIndex(undefined);
      return;
    }
    e.stopPropagation();
    const {
      key
    } = e || {};
    switch (key) {
      case EVENT_KEYS.space:
        if (!typedValue) return;
        if (key.length === 1) {
          var _toggleRef$current;
          (_toggleRef$current = toggleRef.current) === null || _toggleRef$current === void 0 ? void 0 : _toggleRef$current.focus();
          setTypedValue(`${typedValue}${key}`);
          setFocusedIndex(undefined);
          !dropdownMenuVisible && setDropdownMenuVisible(true);
        }
        e.preventDefault();
        break;
      case EVENT_KEYS.backspace:
        setTypedValue(typedValue.substr(0, typedValue.length - 1) || '');
        break;
      case EVENT_KEYS.arrow_down:
        e.preventDefault();
        (_firstFocusRef$curren = firstFocusRef.current) === null || _firstFocusRef$curren === void 0 ? void 0 : _firstFocusRef$curren.focus();
        dropdownMenuVisible && setFocusedIndex({
          renderIndex: 0,
          keyboard: true,
          behavior: 'smooth'
        });
        break;
      case EVENT_KEYS.tab:
        dropdownMenuVisible && setFocusedIndex({
          renderIndex: selectedIndex !== undefined && !disableDeselect ? -1 : 0,
          keyboard: true,
          behavior: 'smooth'
        });
        break;
      case EVENT_KEYS.escape:
        setDropdownMenuVisible(false);
        setTypedValue('');
        (_toggleRef$current2 = toggleRef.current) === null || _toggleRef$current2 === void 0 ? void 0 : _toggleRef$current2.focus();
        break;
      default:
        if (key.length === 1) {
          var _toggleRef$current3;
          (_toggleRef$current3 = toggleRef.current) === null || _toggleRef$current3 === void 0 ? void 0 : _toggleRef$current3.focus();
          setTypedValue(`${typedValue}${key}`);
          setFocusedIndex(undefined);
          !dropdownMenuVisible && setDropdownMenuVisible(true);
        }
    }
  };
  const handleKeyboardNav = (event, focusedRef) => {
    useDropdownMenuKeyboardNav({
      buttonRef: toggleRef,
      focusedElement: focusedRef,
      currentIndex: focusedIndex,
      disableDeselect: disableDeselect,
      event: event,
      onBlur: onBlur,
      options: renderedOptions,
      selectedIndex: selectedIndex,
      setDropdownMenuVisible: setDropdownMenuVisible,
      setFocusIndex: setFocusedIndex,
      setSelectedIndex: setSelectedIndex,
      setTypedValue: setTypedValue
    });
  };
  const handleSelection = (index, focusedRef) => {
    var _focusedRef$current3;
    if (selectedIndex === index) {
      setDropdownMenuVisible(false);
    } else {
      setSelectedIndex(index);
    }
    setTypedValue('');
    // this blur assists with mouse selection to remove the tooltip
    focusedRef === null || focusedRef === void 0 ? void 0 : (_focusedRef$current3 = focusedRef.current) === null || _focusedRef$current3 === void 0 ? void 0 : _focusedRef$current3.blur();
    onBlur && onBlur();
  };
  const renderIcon = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, isDisabled && /*#__PURE__*/React.createElement(SecureIcon, {
      contextType: "image",
      color: boulder
    }), isRequired && !isDisabled && /*#__PURE__*/React.createElement(React.Fragment, null, showRequiredInPlaceholder && [VALIDATION_STATES.neutral, VALIDATION_STATES.pending].includes(valid) ? /*#__PURE__*/React.createElement(ValidationRequiredIcon, {
      contextType: "image",
      color: thunderBird
    }) : valid === VALIDATION_STATES.success ? /*#__PURE__*/React.createElement(ValidIcon, {
      contextType: "image",
      color: apple
    }) : valid === VALIDATION_STATES.error ? /*#__PURE__*/React.createElement(WarningIcon, {
      contextType: "image",
      color: thunderBird
    }) : null));
  };
  const renderDropdownMenu = () => {
    var _toggleRef$current4;
    return /*#__PURE__*/React.createElement(React.Fragment, null, dropdownMenuVisible && /*#__PURE__*/React.createElement(DropdownContainer, null, /*#__PURE__*/React.createElement(DropdownMenu, {
      scale: scale,
      parentWidth: (toggleRef === null || toggleRef === void 0 ? void 0 : (_toggleRef$current4 = toggleRef.current) === null || _toggleRef$current4 === void 0 ? void 0 : _toggleRef$current4.clientWidth) || 0,
      maxMenuHeight: maxMenuHeight,
      title: "select options",
      role: "listbox",
      "aria-activedescendant": selectedIndex !== undefined ? options[selectedIndex].value : undefined,
      tabIndex: -1,
      onKeyDown: e => {
        handleKeyboardNav(e, focusedRef);
      }
    }, !disableDeselect && selectedIndex !== undefined && /*#__PURE__*/React.createElement(ClearOption, {
      ref: createClearOptionRef(),
      tabIndex: 0,
      isFocused: (focusedIndex === null || focusedIndex === void 0 ? void 0 : focusedIndex.renderIndex) === -1,
      onClick: () => setSelectedIndex(undefined),
      onMouseMove: e => {
        setFocusedIndex({
          renderIndex: -1,
          keyboard: false,
          behavior: 'auto'
        });
        e.currentTarget.focus();
      }
    }, deselectText), renderOptions())));
  };
  const renderOptions = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, renderedOptions.length ? renderedOptions.map(opt => /*#__PURE__*/React.createElement(Option, {
      ref: createOptionRef(opt),
      key: opt.value,
      value: opt.value,
      role: "option",
      "aria-label": opt.value,
      "aria-selected": selectedIndex === opt.selectionIndex,
      isFocused: (focusedIndex === null || focusedIndex === void 0 ? void 0 : focusedIndex.renderIndex) === opt.renderIndex,
      tabIndex: 0,
      onMouseDown: () => {
        handleSelection(opt.selectionIndex, focusedRef);
      },
      onMouseMove: e => {
        setFocusedIndex({
          renderIndex: opt.renderIndex,
          keyboard: false,
          behavior: 'auto'
        });
        e.currentTarget.focus();
      }
    }, opt.label)) : /*#__PURE__*/React.createElement(NoOptions, null, "No options"));
  };
  return /*#__PURE__*/React.createElement(GridContainerContext.Consumer, null, _ref2 => {
    let {
      edges
    } = _ref2;
    return loading ? /*#__PURE__*/React.createElement(Skeleton, {
      height: CONTAINER_SIZES[scale || SCALE.default]
    }) : /*#__PURE__*/React.createElement(DropdownWrapper, {
      minWidth: minWidth,
      scale: scale,
      isGrid: !!(edges !== null && edges !== void 0 && edges.length),
      onBlur: e => handleBlur(e),
      onKeyDown: e => handleTextEntry(e)
    }, /*#__PURE__*/React.createElement(FocusRing, {
      focusRingClass: "focus-ring"
    }, /*#__PURE__*/React.createElement(InputButton, {
      ref: toggleRef,
      "aria-label": `${name} select field`,
      isDisabled: isDisabled,
      isRequired: isRequired,
      disableBorder: disableBorder,
      type: "button",
      "aria-haspopup": "listbox",
      "aria-expanded": dropdownMenuVisible,
      scale: scale,
      dropdownVisible: dropdownMenuVisible,
      isGrid: !!(edges !== null && edges !== void 0 && edges.length),
      showRequiredInPlaceholder: showRequiredInPlaceholder,
      hasValue: !!value,
      onClick: () => handleFocus(),
      onFocus: () => setFocusedIndex(undefined),
      validationState: valid
    }, /*#__PURE__*/React.createElement(ValueContainer, null, renderIcon(), /*#__PURE__*/React.createElement("div", null, typedValue ? /*#__PURE__*/React.createElement(DisplayValue, null, typedValue) : selectedIndex !== undefined ? /*#__PURE__*/React.createElement(DisplayValue, null, options[selectedIndex].label) : isDisabled ? placeholder || 'No Selection' : placeholder || '')), /*#__PURE__*/React.createElement(CaretSolidIcon, {
      rotate: dropdownMenuVisible ? '180deg' : '0deg',
      strokeColor: isDisabled ? alto : java,
      color: isDisabled ? alto : java
    }))), /*#__PURE__*/React.createElement(HiddenInput, {
      name: name,
      autoComplete: autoComplete,
      id: id,
      tabIndex: -1,
      onChange: e => setSelection(e.target.value),
      value: value
    }), renderDropdownMenu());
  });
}
function defaultOptionMatcher(value, opt) {
  var _opt$data, _opt$data2, _opt$data3;
  if (typeof value === 'undefined') {
    return false;
  }
  const optionValues = [(_opt$data = opt.data) === null || _opt$data === void 0 ? void 0 : _opt$data.iso3, (_opt$data2 = opt.data) === null || _opt$data2 === void 0 ? void 0 : _opt$data2.iso2, (_opt$data3 = opt.data) === null || _opt$data3 === void 0 ? void 0 : _opt$data3.name, opt.value].filter(item => typeof item !== 'undefined').map(val => (typeof val === 'string' ? val : JSON.stringify(val)).toLowerCase());
  const selectedValue = (typeof value === 'string' ? value : JSON.stringify(value)).toLowerCase();
  return optionValues.includes(selectedValue);
}
function getItemIndex(value, options) {
  let matcher = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultOptionMatcher;
  const index = options.findIndex(opt => matcher(value, opt));
  return index >= 0 ? index : undefined;
}
const getBackgroundColor = (dropdownVisible, isDisabled, isRequired, validationState) => {
  if (isDisabled) {
    return mercury;
  } else if (dropdownVisible) {
    return catskillWhite;
  } else if (!isRequired || [VALIDATION_STATES.neutral, VALIDATION_STATES.pending].includes(validationState)) {
    return 'inherit';
  } else {
    return validationState === VALIDATION_STATES.success ? willowBrook : fairPink;
  }
};
const getBoxShadow = (dropdownVisible, disableBorder, isDisabled, isRequired, validationState) => {
  if (isDisabled) {
    return `inset 50px 0 0 -43px ${alto}, inset 0 0 0 1px ${alto}`;
  } else if (dropdownVisible) {
    return `0 0 5px ${bondiBlue}${disableBorder ? '' : `, inset 0 0 0 1px ${alto}`}`;
  } else if (!isRequired || [VALIDATION_STATES.neutral, VALIDATION_STATES.pending].includes(validationState)) {
    return disableBorder ? 'unset' : `inset 0 0 0 1px ${alto}`;
  } else {
    const borderAppendix = disableBorder ? '' : `, ${alto} 0 0 0 1px inset`;
    return validationState === VALIDATION_STATES.success ? `${apple} 50px 0 0 -43px inset${borderAppendix}` : `${thunderBird} 50px 0 0 -43px inset${borderAppendix}`;
  }
};
const DisplayValue = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropdown__DisplayValue",
  componentId: "sc-4q0br9-0"
})([""]);
const DropdownWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropdown__DropdownWrapper",
  componentId: "sc-4q0br9-1"
})(["border-radius:inherit;font-family:\"Open Sans\",Arial,Helvetica,sans-serif;font-size:16px;height:", ";margin-top:", ";margin-left:", ";min-width:", ";"], _ref3 => {
  let {
    scale
  } = _ref3;
  return CONTAINER_SIZES[scale || SCALE.default];
}, _ref4 => {
  let {
    isGrid
  } = _ref4;
  return isGrid ? '-1px' : '0';
}, _ref5 => {
  let {
    isGrid
  } = _ref5;
  return isGrid ? '-1px' : '0';
}, _ref6 => {
  let {
    minWidth
  } = _ref6;
  return minWidth;
});
const InputButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "Dropdown__InputButton",
  componentId: "sc-4q0br9-2"
})(["align-items:center;background-color:", ";border:none;border-radius:", ";box-shadow:", ";box-sizing:border-box;color:", ";display:flex;font-size:", ";font-weight:", ";height:100%;justify-content:space-between;line-height:", ";outline:none;padding:.875em;text-align:left;width:100%;&.focus-ring{outline:2px solid ", ";outline-offset:2px;}@media (min-width:", "){cursor:", ";}"], _ref7 => {
  let {
    dropdownVisible,
    isDisabled,
    isRequired,
    validationState
  } = _ref7;
  return getBackgroundColor(dropdownVisible, !!isDisabled, !!isRequired, validationState);
}, _ref8 => {
  let {
    scale,
    isGrid
  } = _ref8;
  return isGrid ? 'inherit' : RADIUS_SIZES[scale || 'default'];
}, _ref9 => {
  let {
    dropdownVisible,
    disableBorder,
    isDisabled,
    isRequired,
    validationState
  } = _ref9;
  return getBoxShadow(dropdownVisible, !!disableBorder, !!isDisabled, !!isRequired, validationState);
}, _ref10 => {
  let {
    hasValue
  } = _ref10;
  return hasValue ? mineShaft : boulder;
}, _ref11 => {
  let {
    scale
  } = _ref11;
  return FONT_SIZES[scale || SCALE.default];
}, _ref12 => {
  let {
    hasValue
  } = _ref12;
  return hasValue ? '600' : '400';
}, _ref13 => {
  let {
    scale
  } = _ref13;
  return LINE_HEIGHT_SIZES[scale || SCALE.default];
}, java, breakpoints.smallMax, _ref14 => {
  let {
    isDisabled
  } = _ref14;
  return isDisabled ? 'not-allowed' : 'pointer';
});
const ValueContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropdown__ValueContainer",
  componentId: "sc-4q0br9-3"
})(["display:flex;align-items:center;svg{margin-right:8px;}"]);
const HiddenInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "Dropdown__HiddenInput",
  componentId: "sc-4q0br9-4"
})(["opacity:0;position:absolute;top:0;z-index:-1;"]);
const DropdownContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropdown__DropdownContainer",
  componentId: "sc-4q0br9-5"
})(["margin-top:10px;position:absolute;width:100%;z-index:1;"]);
const DropdownMenu = /*#__PURE__*/styled.ul.withConfig({
  displayName: "Dropdown__DropdownMenu",
  componentId: "sc-4q0br9-6"
})(["width:", ";background-color:", ";border-radius:", ";box-shadow:inset 0 0 0 1px ", ";font-size:", ";overflow-y:auto;margin:0;max-height:", ";padding:0;::before{content:\" \";position:absolute;bottom:100%;left:", "px;margin-left:-8px;border-width:8px;border-style:solid;border-color:transparent transparent ", " transparent;}"], _ref15 => {
  let {
    parentWidth
  } = _ref15;
  return parentWidth + 'px' || 'auto';
}, white, _ref16 => {
  let {
    scale
  } = _ref16;
  return RADIUS_SIZES[scale || 'default'];
}, alto, _ref17 => {
  let {
    scale
  } = _ref17;
  return FONT_SIZES[scale || SCALE.default];
}, _ref18 => {
  let {
    maxMenuHeight
  } = _ref18;
  return maxMenuHeight;
}, _ref19 => {
  let {
    parentWidth
  } = _ref19;
  return parentWidth / 2;
}, alto);
const ClearOption = /*#__PURE__*/styled.li.withConfig({
  displayName: "Dropdown__ClearOption",
  componentId: "sc-4q0br9-7"
})(["background-color:", ";border-bottom:1px solid ", ";color:", ";padding:.875em;&:focus{outline:none;}@media (min-width:", "){cursor:pointer;&:hover{outline:none;}}"], _ref20 => {
  let {
    isFocused
  } = _ref20;
  return isFocused ? java : 'unset';
}, alto, _ref21 => {
  let {
    isFocused
  } = _ref21;
  return isFocused ? white : boulder;
}, breakpoints.smallMax);
const Option = /*#__PURE__*/styled.li.withConfig({
  displayName: "Dropdown__Option",
  componentId: "sc-4q0br9-8"
})(["background-color:", ";color:", ";list-style:none;outline:none;padding:.875em;&[aria-selected=\"true\"]{background-color:", ";border-left:1px solid ", ";border-right:1px solid ", ";box-shadow:", ";}&:first-of-type[aria-selected=\"true\"]{border-top:1px solid ", ";border-top-left-radius:inherit;border-top-right-radius:inherit;}&:last-of-type[aria-selected=\"true\"]{border-bottom:1px solid ", ";border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;}@media (min-width:", "){cursor:pointer;}"], _ref22 => {
  let {
    isFocused
  } = _ref22;
  return isFocused ? java : 'unset';
}, _ref23 => {
  let {
    isFocused
  } = _ref23;
  return isFocused ? white : boulder;
}, _ref24 => {
  let {
    isFocused
  } = _ref24;
  return isFocused ? java : catskillWhite;
}, alto, alto, _ref25 => {
  let {
    isFocused
  } = _ref25;
  return isFocused ? 'unset' : `inset 50px 1px 0 -43px ${frenchPass}`;
}, alto, alto, breakpoints.smallMax);
const NoOptions = /*#__PURE__*/styled.li.withConfig({
  displayName: "Dropdown__NoOptions",
  componentId: "sc-4q0br9-9"
})(["color:", ";list-style:none;outline:none;padding:.875em;text-align:center;@media (min-width:", "){cursor:not-allowed;}"], boulder, breakpoints.smallMax);